import React, { useMemo, useRef } from "react";
import { Download, X } from "lucide-react";

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/<[^>]+>/g, "")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function productLabel(value) {
  const map = { banao: "Banao", interviewgod: "InterviewGod", generic: "Generic", other: "Other" };
  return map[value] || value || "Generic";
}

function formatDate(value) {
  if (!value) return "Just Now";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function normaliseMarkdown(value) {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/^\s+\*\s+/gm, "- ");
}

function evidenceText(value) {
  return String(value).split(/(\*\*[^*]+\*\*|\[C\]|\[I\])/g).map((part, index) => {
    if (part === "[C]") return <span key={`${part}-${index}`} className="tag-confirmed">[C]</span>;
    if (part === "[I]") return <span key={`${part}-${index}`} className="tag-inferred">[I]</span>;
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={`${part}-${index}`}>{evidenceText(part.slice(2, -2))}</strong>;
    return part;
  });
}

function splitTableRow(line) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function parseMarkdownBlocks(markdown) {
  const lines = normaliseMarkdown(markdown).split("\n");
  const blocks = [];

  for (let index = 0; index < lines.length;) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith("|") && lines[index + 1]?.trim().match(/^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/)) {
      const headers = splitTableRow(trimmed);
      index += 2;
      const rows = [];
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      blocks.push({ type: "table", headers, rows });
      continue;
    }

    if (/^#{1,3}\s+/.test(trimmed)) {
      const level = trimmed.match(/^#+/)[0].length;
      const text = trimmed.replace(/^#{1,3}\s+/, "");
      blocks.push({ type: "heading", level, text });
      index += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*]\s+/, ""));
        index += 1;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    const paragraph = [trimmed];
    index += 1;
    while (
      index < lines.length
      && lines[index].trim()
      && !/^#{1,3}\s+/.test(lines[index].trim())
      && !/^[-*]\s+/.test(lines[index].trim())
      && !lines[index].trim().startsWith("|")
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ type: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
}

function MarkdownReport({ markdown }) {
  const blocks = useMemo(() => parseMarkdownBlocks(markdown), [markdown]);

  return blocks.map((block, index) => {
    if (block.type === "heading") {
      if (block.level === 1) return <h1 key={index} className="dossier-title">{evidenceText(block.text)}</h1>;
      if (block.level === 2) return <h2 key={index} id={slugify(block.text)}>{evidenceText(block.text)}</h2>;
      return <h3 key={index}>{evidenceText(block.text)}</h3>;
    }
    if (block.type === "table") {
      return (
        <table key={index}>
          <thead>
            <tr>{block.headers.map((header, cellIndex) => <th key={cellIndex}>{evidenceText(header)}</th>)}</tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {block.headers.map((_, cellIndex) => <td key={cellIndex}>{evidenceText(row[cellIndex] || "")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (block.type === "list") {
      return <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{evidenceText(item)}</li>)}</ul>;
    }
    return <p key={index}>{evidenceText(block.text)}</p>;
  });
}

export default function DossierViewer({ dossier, onDownload, onClose }) {
  const contentRef = useRef(null);
  const markdown = dossier?.dossier_markdown || "";
  const sections = useMemo(() => {
    const matches = [...markdown.matchAll(/^##\s+(.+)$/gm)];
    if (!matches.length) return [{ label: "Overview", id: "overview" }];
    return matches.map((match) => {
      const label = match[1].trim();
      return { label, id: slugify(label) };
    });
  }, [markdown]);

  const scrollTo = (id) => {
    const target = contentRef.current?.querySelector(`#${CSS.escape(id)}`);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="dossier-viewer">
      <header className="dossier-viewer-header">
        <div>
          <h2>Prospect Dossier</h2>
          <p>
            {dossier?.prospect_name} - {dossier?.prospect_company} - {productLabel(dossier?.product_context)} - Generated {formatDate(dossier?.completed_at)}
          </p>
        </div>
        <div className="dossier-viewer-actions">
          <button className="Outline-Button Small" onClick={() => onDownload(dossier.id || dossier.dossier_id, dossier.prospect_name)}>
            <Download size={14} /> Download .md
          </button>
          <button className="Soft-Button Small" onClick={onClose}><X size={14} /> Close</button>
        </div>
      </header>

      <div className="dossier-viewer-shell">
        <aside className="dossier-toc">
          <strong>Contents</strong>
          {sections.map((item, index) => (
            <button type="button" key={item.id || index} onClick={() => scrollTo(item.id)}>
              {item.label.replace(/^\d+\s*-\s*/, `${index + 1}. `)}
            </button>
          ))}
        </aside>
        <article className="dossier-content" ref={contentRef}>
          <MarkdownReport markdown={markdown} />
          <footer className="dossier-meta">
            Generated by {dossier?.generated_by_name || "Sales Team"} - {dossier?.generation_time_seconds || "-"}s
          </footer>
        </article>
      </div>
    </div>
  );
}
